/**
 * XT timeframe configuration and interval mapping.
 * @see https://doc.xt.com/docs/spot/Market/GetKlineData
 */

import type { XtWsKlineInterval } from './ws';

import {
  registerExchangeTimeframes,
  resolutionToSeconds,
  type TimeframeConfig,
} from '../../lib/timeframes';

/**
 * XT supported kline intervals.
 */
export const XT_TIMEFRAMES: TimeframeConfig = {
  native: [
    '1m',
    '3m',
    '5m',
    '15m',
    '30m',
    '1h',
    '2h',
    '4h',
    '6h',
    '8h',
    '12h',
    '1d',
    '3d',
    '1w',
    '1M',
  ],
  synthetic: ['1s'], // Use ticker for 1s
  minIntervalSeconds: 60,
};

// Register with central registry
registerExchangeTimeframes('xt', XT_TIMEFRAMES);

/**
 * Maps TradingView resolution to XT interval format.
 */
export const mapToXtInterval = (resolution: string): XtWsKlineInterval => {
  // Return type matches XT WebSocket interval union
  const seconds = resolutionToSeconds(resolution);

  if (seconds === 60) return '1m';
  if (seconds === 180) return '3m';
  if (seconds === 300) return '5m';
  if (seconds === 900) return '15m';
  if (seconds === 1800) return '30m';
  if (seconds === 3600) return '1h';
  if (seconds === 7200) return '2h';
  if (seconds === 14400) return '4h';
  if (seconds === 21600) return '6h';
  if (seconds === 28800) return '8h';
  if (seconds === 43200) return '12h';
  if (seconds === 86400) return '1d';
  if (seconds === 259200) return '3d';
  if (seconds === 604800) return '1w';
  if (seconds === 2592000) return '1M';

  // Default to closest supported interval
  if (seconds < 60) return '1m';
  if (seconds < 180) return '1m';
  if (seconds < 300) return '3m';
  if (seconds < 900) return '5m';
  if (seconds < 1800) return '15m';
  if (seconds < 3600) return '30m';
  if (seconds < 7200) return '1h';
  if (seconds < 14400) return '2h';
  if (seconds < 21600) return '4h';
  if (seconds < 28800) return '6h';
  if (seconds < 43200) return '8h';
  if (seconds < 86400) return '12h';
  if (seconds < 259200) return '1d';
  if (seconds < 604800) return '3d';
  if (seconds < 2592000) return '1w';

  return '1M';
};
